import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ClientOnly } from "@/components/ClientOnly";
import { RouterProvider } from "@/components/RouterProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PasswordRecoveryDialog } from "@/components/PasswordRecoveryDialog";
import { CookieConsent } from "@/components/CookieConsent";
import { LocaleProvider } from "@/i18n/context";
import { CHECKOUT_PATHS, DASHBOARD_ROUTE_SEGMENTS } from "@/i18n/routes";
import Index from "./pages/Index";
import GuideHub from "./pages/GuideHub";
import GuideArticle from "./pages/GuideArticle";
import { guideIndex } from "@/content/guides/registry";
import RatePage from "./pages/RatePage";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import DataDeletion from "./pages/DataDeletion";

// Keep the landing page eager for best SSR/SEO, but lazy-load low-frequency/heavy routes.
// Customer dashboard — sidebar shell with nested sub-pages (Overview/Reports/Credits + detail).
const DashboardLayout = lazy(() => import("./features/dashboard/components/shell/DashboardLayout"));
const DashOverview = lazy(() => import("./features/dashboard/pages/OverviewPage"));
const DashReports = lazy(() => import("./features/dashboard/pages/ReportsPage"));
const DashCredits = lazy(() => import("./features/dashboard/pages/CreditsPage"));
const DashReportDetail = lazy(() => import("./features/dashboard/pages/ReportDetailRoute"));
// Admin panel — consolidated under src/features/admin with a sidebar shell.
const AdminLayout = lazy(() => import("./features/admin/components/shell/AdminLayout"));
const AdminOverview = lazy(() => import("./features/admin/pages/OverviewPage"));
const AdminOrders = lazy(() => import("./features/admin/pages/OrdersPage"));
const AdminOrderDetail = lazy(() => import("./features/admin/pages/OrderDetailPage"));
const AdminJobs = lazy(() => import("./features/admin/pages/JobsPage"));
const AdminUsers = lazy(() => import("./features/admin/pages/UsersPage"));
const AdminAnalytics = lazy(() => import("./features/admin/pages/AnalyticsPage"));
const AdminSystem = lazy(() => import("./features/admin/pages/SystemHealthPage"));
const AdminAudit = lazy(() => import("./features/admin/pages/AuditLogsPage"));
const AdminExport = lazy(() => import("./features/admin/pages/ExportPage"));
const AdminEmails = lazy(() => import("./features/admin/pages/EmailsPage"));
const AdminPrompts = lazy(() => import("./features/admin/pages/PromptsPage"));
const AdminReportTemplate = lazy(() => import("./features/admin/pages/ReportTemplatePage"));
const AdminDocs = lazy(() => import("./features/admin/pages/DocsPage"));
const AdminDocArticle = lazy(() => import("./features/admin/pages/DocArticlePage"));

const SharedReport = lazy(() => import("./pages/SharedReport"));

const Checkout = lazy(() =>
  import("./pages/Checkout").then((m) => ({ default: m.Checkout }))
);
const PaymentSuccess = lazy(() =>
  import("./pages/PaymentSuccess").then((m) => ({ default: m.PaymentSuccess }))
);
const PaymentCanceled = lazy(() =>
  import("./pages/PaymentCanceled").then((m) => ({ default: m.PaymentCanceled }))
);

function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-4">
        <Skeleton className="mx-auto h-10 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4 mx-auto" />
        <p className="text-center text-sm text-muted-foreground pt-2">Nalaganje...</p>
      </div>
    </div>
  );
}

function withSuspense(element: React.ReactElement) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: 1000,
      // Reduce refetch churn for mostly-read UIs (esp. admin/dashboard).
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

type AppProps = {
  /** SSR-only: current request path */
  ssrLocation?: string;
};

const App = ({ ssrLocation }: AppProps) => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <RouterProvider ssrLocation={ssrLocation}>
          <LocaleProvider initialPath={ssrLocation}>
            <ScrollToTop />
            <PasswordRecoveryDialog />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/en" element={<Index />} />
              <Route path="/de" element={<Index />} />
              <Route path="/ru" element={<Index />} />
              <Route path="/oceni" element={<RatePage />} />
              {/* Content hub (SSR/prerendered for GEO) — hub + cornerstone guides. */}
              {guideIndex().map((g) => (
                <Route
                  key={g.path}
                  path={g.path}
                  element={g.isHub ? <GuideHub /> : <GuideArticle />}
                />
              ))}
              {DASHBOARD_ROUTE_SEGMENTS.map((d) => (
                <Route key={d.base} path={d.base} element={withSuspense(<DashboardLayout />)}>
                  <Route index element={withSuspense(<DashOverview />)} />
                  <Route path={d.reports} element={withSuspense(<DashReports />)} />
                  <Route path={d.credits} element={withSuspense(<DashCredits />)} />
                  <Route path={d.detail} element={withSuspense(<DashReportDetail />)} />
                </Route>
              ))}
              <Route
                path="/dashboard"
                element={<Navigate to="/nadzorna-plosca" replace />}
              />
              <Route path="/admin" element={withSuspense(<AdminLayout />)}>
                <Route index element={withSuspense(<AdminOverview />)} />
                <Route path="orders" element={withSuspense(<AdminOrders />)} />
                <Route
                  path="orders/:orderId"
                  element={withSuspense(<AdminOrderDetail />)}
                />
                <Route path="jobs" element={withSuspense(<AdminJobs />)} />
                <Route path="users" element={withSuspense(<AdminUsers />)} />
                <Route path="analytics" element={withSuspense(<AdminAnalytics />)} />
                <Route path="system" element={withSuspense(<AdminSystem />)} />
                <Route path="audit" element={withSuspense(<AdminAudit />)} />
                <Route path="export" element={withSuspense(<AdminExport />)} />
                <Route path="emails" element={withSuspense(<AdminEmails />)} />
                <Route path="prompts" element={withSuspense(<AdminPrompts />)} />
                <Route
                  path="report-template"
                  element={withSuspense(<AdminReportTemplate />)}
                />
                <Route path="docs" element={withSuspense(<AdminDocs />)} />
                <Route path="docs/:slug" element={withSuspense(<AdminDocArticle />)} />
              </Route>
              <Route path="/r/:token" element={withSuspense(<SharedReport />)} />
              {CHECKOUT_PATHS.map(path => (
                <Route key={path} path={path} element={withSuspense(<Checkout />)} />
              ))}
              {["/payment-success", "/en/payment-success", "/de/payment-success", "/ru/payment-success"].map(
                (path) => (
                  <Route key={path} path={path} element={withSuspense(<PaymentSuccess />)} />
                ),
              )}
              <Route
                path="/payment-canceled"
                element={withSuspense(<PaymentCanceled />)}
              />
              <Route path="/varovanje-podatkov" element={<PrivacyPolicy />} />
              <Route path="/splosni-pogoji" element={<TermsOfService />} />
              <Route path="/piskotki" element={<CookiePolicy />} />
              <Route path="/brisanje-podatkov" element={<DataDeletion />} />
              <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/en/terms-of-service" element={<TermsOfService />} />
              <Route path="/en/cookie-policy" element={<CookiePolicy />} />
              <Route path="/en/data-deletion" element={<DataDeletion />} />
              <Route path="/de/datenschutz" element={<PrivacyPolicy />} />
              <Route path="/de/agb" element={<TermsOfService />} />
              <Route path="/de/cookies" element={<CookiePolicy />} />
              <Route path="/de/datenloeschung" element={<DataDeletion />} />
              <Route path="/ru/konfidencialnost" element={<PrivacyPolicy />} />
              <Route path="/ru/usloviya" element={<TermsOfService />} />
              <Route path="/ru/cookies" element={<CookiePolicy />} />
              <Route path="/ru/udalenie-dannyh" element={<DataDeletion />} />
              <Route path="*" element={<NotFound />} />
            </Routes>

            <Toaster />
            <ClientOnly>
              <Sonner />
              {/* Client-only: the banner's visibility depends on localStorage,
                  and these pages are prerendered. */}
              <CookieConsent />
            </ClientOnly>
          </LocaleProvider>
        </RouterProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;

